import { Component, HostBinding } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { NotificationType } from '../../shared/models/notification-type';
import { NotificationService } from '../../shared/services/notification/notification.service';
import { UserService } from '../../shared/services/user/user.service';
import { IChangePasswordFormInterface } from './models/change-password-form.interface';
import { ChangePasswordService } from './services/change-password.service';

@Component({
    selector: 'upsc-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent {
    @HostBinding('class') public hostClass = 'change-password';

    public formGroup: FormGroup<IChangePasswordFormInterface>;
    public isFormSubmitting = false;

    public constructor(private readonly formBuilder: FormBuilder,
                       private readonly translateService: TranslateService,
                       private readonly changePasswordService: ChangePasswordService,
                       private readonly userService: UserService,
                       private readonly notificationService: NotificationService,
    ) {
        this.initForm();
        this.translateService.onLangChange.subscribe(() => {
            this.updateForm();
        });
    }

    public onFormSubmit(event: SubmitEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        let username: string;
        const getUser = this.userService.getCurrentUser().pipe(
            catchError((error) => {
                this.notificationService.notify(
                    'Error changing password. Please contact support.',
                    'Error',
                    NotificationType.ERROR,
                );

                return EMPTY; // Use EMPTY to stop the observable chain in case of error
            }),
            tap((user) => {
                username = user?.username;
            }),
        );

        const validatePassword = (user): Observable<boolean> => {
            if (!username) {return of(false);}

            const currentPassword = this.formGroup.controls.currentPassword.value;

            return this.changePasswordService.validatePassword(username, currentPassword).pipe(
                catchError((error) => {
                    this.notificationService.notify(
                        'Error validating password. Please try again.',
                        'Error',
                        NotificationType.ERROR,
                    );

                    return EMPTY; // Use EMPTY to stop the observable chain in case of error
                }),
                tap((isValid) => {
                    if (!isValid) {
                        this.notificationService.notify(
                            'Error validating password. Please try again.',
                            'Error',
                            NotificationType.ERROR,
                        );
                    }
                }),
            );
        };

        const updatePassword = (isValid): Observable<boolean> => {
            if (!isValid) {return of(false);}

            const newPassword = this.formGroup.controls.newPassword.value;

            return this.changePasswordService.updatePassword(username, newPassword).pipe(
                catchError((error) => {
                    this.notificationService.notify(
                        'Error updating password. Please try again.',
                        'Error',
                        NotificationType.ERROR,
                    );

                    return of(false); // Continue the chain even in case of error
                }),
                tap((isSuccess) => {
                    if (!isSuccess) {
                        this.notificationService.notify(
                            'Error updating password. Please try again.',
                            'Error',
                            NotificationType.ERROR,
                        );
                    }
                }),
            );
        };

        this.isFormSubmitting = true;

        getUser.pipe(
            switchMap(userName => validatePassword(userName)),
            switchMap(isValid => updatePassword(isValid)),
            finalize(() => this.isFormSubmitting = false),
        ).subscribe((isSuccess) => {
            if (!isSuccess) {
                return;
            }

            this.notificationService.notify(
                'Password has been updated.',
                'Success',
                NotificationType.SUCCESS,
            );

            this.resetForm();
        });
    }

    private initForm(): void {
        this.updateForm();
    }

    updateForm(): void {
        const passwordRequiredText = this.translateService.instant('passwordValidation.passwordRequired');
        this.formGroup = this.formBuilder.group<IChangePasswordFormInterface>({
            currentPassword: new FormControl<string>(null, RxwebValidators.required({ message: passwordRequiredText })),
            newPassword: new FormControl<string>(null, [
                RxwebValidators.required({ message: passwordRequiredText }),
                RxwebValidators.password({
                    validation: {
                        minLength: 8,
                        digit: true,
                        alphabet: true,
                    },
                    message: {
                        minLength: this.translateService.instant('passwordValidation.minLength', { minPasswordLength: 8 }),
                        digit: this.translateService.instant('passwordValidation.digit', { minDigitCount: 1 }),
                        alphabet: this.translateService.instant('passwordValidation.alphabet', { minAlphabetCount: 1 }),
                    },
                }),
            ]),
            confirmNewPassword: new FormControl<string>(null, [
                RxwebValidators.compare({
                    fieldName: 'newPassword',
                    message: this.translateService.instant('passwordValidation.passwordNotMatched'),
                }),
            ]),
        });
    }

    private resetForm(): void {
        this.formGroup.reset({}, { emitEvent: false });
        this.formGroup.markAsPristine();
        this.formGroup.markAsUntouched();
        Object.entries(this.formGroup.controls).forEach(([key, value]) => {
            (value as AbstractControl).setErrors(null);
        });
    }
}
